import { useEffect, useState, useCallback, useRef } from 'react';
import type { BannerContentConfig, GetActiveBannerMessagesResponse } from '@enter/api';
import { NOTIFICATION_BANNER_VARIANT } from './types';
import type { NotificationBannerProps, NotificationBannerCarouselItem } from './types';
import { addReadId, getReadIds } from './read-ids-storage';

type GetActiveBannerMessages = () => Promise<GetActiveBannerMessagesResponse>;

type MarkBannerAsRead = (promoMessageIds: number[]) => Promise<{
    code: number;
    message: string;
    data: unknown;
}>;

export interface UseNotificationBannerOptions {
    /** CTA 點擊回調，由調用方處理外鏈/內鏈跳轉或其他操作 */
    onCtaClick?: (banner: NotificationBannerProps) => void;
    /** 當前展示頁面，用於篩選 displayPages 包含此頁面的橫幅（如 'workspace' | 'landing'） */
    displayPage?: string;
    /** 变化时重新请求，用于同一 React mount 内切换 Workspace 或语言。 */
    requestKey?: string | number;
    /** Changes that should drop the current display immediately, such as workspace identity. */
    identityKey?: string | number;
    /** false 时保持空展示且不请求，重新变为 true 后执行实时读取。 */
    enabled?: boolean;
    /** Anonymous landing may persist dismissals; authenticated live feeds should use the server. */
    persistReadIds?: boolean;
    /** Frontend-only CTA fallback from the language pack. Not backend copy. */
    defaultCtaText?: string;
    /** Request identity compatibility only; copy selection is owned by Backend. */
    locale?: string;
}

export interface UseNotificationBannerReturn {
    /** 當前應顯示的橫幅（包含全部可輪播 Banner） */
    banner: NotificationBannerProps | null;
    /** 關閉指定 id 的橫幅並標記為已讀 */
    onClose: (id: number) => void;
    /** 是否已完成首次加載（用於區分初始化中 / 已就緒） */
    isInitialized: boolean;
}

function getBannerLinkConfig(
    cfg: BannerContentConfig,
    currentDisplayPage: string,
    defaultCtaText: string
): Pick<NotificationBannerCarouselItem, 'ctaText' | 'ctaLink' | 'ctaLinkType'> {
    if (currentDisplayPage === 'landing' && cfg.landingButton) {
        return {
            ctaText: cfg.landingButton.text || defaultCtaText,
            ctaLink: cfg.landingButton.url,
            ctaLinkType: 'externalLink',
        };
    }

    if (currentDisplayPage === 'workspace' && cfg.workspaceButton) {
        return {
            ctaText: cfg.workspaceButton.text || defaultCtaText,
            ctaLink: cfg.workspaceButton.url,
            ctaLinkType:
                cfg.workspaceButton.action === 'internal' ? 'internalLink' : 'externalLink',
        };
    }

    return {
        ctaText: defaultCtaText,
        ctaLink: undefined,
        ctaLinkType: 'externalLink',
    };
}

function mapMessageToBannerItem(
    msg: GetActiveBannerMessagesResponse['data']['messages'][number],
    currentDisplayPage: string,
    defaultCtaText: string
): NotificationBannerCarouselItem {
    const cfg = msg.content_config;
    return {
        id: String(msg.id),
        message: cfg.text ?? '',
        variant: NOTIFICATION_BANNER_VARIANT,
        showCloseButton: cfg.showCloseButton ?? true,
        countdownEndTime: msg.countdown_end_time,
        showCta: !!cfg.showButton,
        ...getBannerLinkConfig(cfg, currentDisplayPage, defaultCtaText),
    };
}

/**
 * 獲取並適配 Notification Banner 的 Hook
 * 調用方需注入 getActiveBannerMessages、markBannerAsRead，避免 business-components 直接依賴 enter-web 的 codeApi
 */
export function useNotificationBanner(
    getActiveBannerMessages: GetActiveBannerMessages,
    markBannerAsRead: MarkBannerAsRead,
    options?: UseNotificationBannerOptions
): UseNotificationBannerReturn {
    const {
        onCtaClick,
        displayPage = 'workspace',
        requestKey,
        identityKey = requestKey,
        enabled = true,
        persistReadIds = true,
        defaultCtaText = 'Read more',
    } = options ?? {};

    const [banner, setBanner] = useState<NotificationBannerProps | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // 使用 ref 存儲回調，避免依賴變化觸發重新獲取
    const getActiveBannerMessagesRef = useRef(getActiveBannerMessages);
    const onCtaClickRef = useRef(onCtaClick);
    const markBannerAsReadRef = useRef(markBannerAsRead);
    const displayPageRef = useRef(displayPage);
    const persistReadIdsRef = useRef(persistReadIds);
    const defaultCtaTextRef = useRef(defaultCtaText);

    // 每次渲染更新 ref，確保始終使用最新的回調
    getActiveBannerMessagesRef.current = getActiveBannerMessages;
    onCtaClickRef.current = onCtaClick;
    markBannerAsReadRef.current = markBannerAsRead;
    displayPageRef.current = displayPage;
    persistReadIdsRef.current = persistReadIds;
    defaultCtaTextRef.current = defaultCtaText;

    const requestSequenceRef = useRef(0);

    const fetchBanner = useCallback(async (requestSequence: number) => {
        try {
            const res = await getActiveBannerMessagesRef.current();
            if (requestSequence !== requestSequenceRef.current) return;
            if (res.code !== 0 || !res.data?.messages) {
                setBanner(null);
                return;
            }

            // 按 priority / id 排序後，把可展示且未讀的消息都交給 Banner 做整體輪播
            const currentDisplayPage = displayPageRef.current;
            const readIds = persistReadIdsRef.current ? getReadIds() : new Set<number>();
            const bannerItems = res.data.messages
                .filter((msg) => {
                    if (msg.message_type !== 'banner') return false;
                    const cfg = msg.content_config;
                    if (!cfg.text?.trim()) return false;
                    const pages = cfg.displayPages ?? [];
                    if (pages.length > 0 && !pages.includes(currentDisplayPage)) return false;
                    if (readIds.has(msg.id)) return false;
                    return true;
                })
                .sort((a, b) => b.priority - a.priority || b.id - a.id)
                .map((msg) =>
                    mapMessageToBannerItem(msg, currentDisplayPage, defaultCtaTextRef.current)
                );

            if (bannerItems.length === 0) {
                setBanner(null);
                return;
            }

            const primaryBanner = bannerItems[0] as NotificationBannerCarouselItem;
            const intervalMs =
                res.data.messages
                    .map((msg) => msg.content_config?.carouselIntervalMs)
                    .find((value): value is number => typeof value === 'number' && value > 0) ??
                3000;

            const bannerProps: NotificationBannerProps = {
                ...primaryBanner,
                carouselItems: bannerItems.length > 1 ? bannerItems : undefined,
                carouselIntervalMs: intervalMs,
                onCtaClick: onCtaClickRef.current,
            };

            setBanner(bannerProps);
        } catch (error) {
            if (requestSequence !== requestSequenceRef.current) return;
            console.error('[useNotificationBanner] failed to fetch banners:', error);
            setBanner(null);
        }
    }, []);

    const identityKeyRef = useRef(identityKey);

    useEffect(() => {
        const requestSequence = ++requestSequenceRef.current;
        setIsInitialized(false);
        if (identityKeyRef.current !== identityKey) {
            identityKeyRef.current = identityKey;
            setBanner(null);
        }
        if (!enabled) {
            setBanner(null);
            return;
        }

        async function init() {
            await fetchBanner(requestSequence);
            if (requestSequence !== requestSequenceRef.current) return;
            setIsInitialized(true);
        }
        void init();
    }, [
        defaultCtaText,
        displayPage,
        enabled,
        fetchBanner,
        identityKey,
        persistReadIds,
        requestKey,
    ]);

    const onClose = useCallback((id: number) => {
        if (persistReadIdsRef.current) addReadId(id);
        setBanner(null);
        markBannerAsReadRef.current([id]).catch((err) => {
            console.error(err);
        });
    }, []);

    return { banner, onClose, isInitialized };
}
